import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Container from '@mui/material/Container';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { BackIcon } from '../../components/AppHeader';
import { useTranslation } from 'react-i18next';
import { getMatchStaff, updateMatchStaff } from '../../api';
import { getFlagSrc } from '../../utils/scoring';
import CountrySelect from '../../components/CountrySelect';
import SaveButton from '../../components/SaveButton';
import Snackbar from '../../components/Snackbar';

const ROLES = ['ro', 'cro', 'so', 'qm', 'rm', 'md'];
const ROLE_LABELS = { ro: 'RO', cro: 'CRO', so: 'SO', qm: 'QM', rm: 'RM', md: 'MD' };
const PERMISSIONS = [
  'squads', 'registration', 'registration-verify', 'registration-change-verified',
  'stages', 'stages-force', 'score', 'manage', 'staff', 'approve-rescoring', 'chrono', 'payments', 'stats',
];
const PERMISSION_KEYS = {
  squads: 'manage.permSquads',
  registration: 'manage.permRegistration',
  'registration-verify': 'manage.permRegistrationVerify',
  'registration-change-verified': 'manage.permRegistrationChangeVerified',
  stages: 'manage.permStages',
  'stages-force': 'manage.permStagesForce',
  score: 'manage.permScore',
  manage: 'manage.permManage',
  staff: 'manage.permStaff',
  'approve-rescoring': 'manage.permApproveRescoring',
  chrono: 'manage.permChrono',
  payments: 'manage.permPayments',
  stats: 'manage.permStats',
};

const PASSWORD_WORDS = [
  'alpha', 'bravo', 'charlie', 'delta', 'echo', 'foxtrot', 'golf', 'hotel',
  'india', 'juliet', 'kilo', 'lima', 'mike', 'november', 'oscar', 'papa',
  'quebec', 'romeo', 'sierra', 'tango', 'uniform', 'victor', 'whiskey', 'xray',
  'yankee', 'zulu',
];

function randomPassword() {
  const word = PASSWORD_WORDS[Math.floor(Math.random() * PASSWORD_WORDS.length)];
  const num = Math.floor(Math.random() * 900) + 100;
  return `${word}${num}`;
}

const emptyStaff = () => ({
  publicId: '',
  countryCode: '',
  publicIdNumber: '',
  name: '',
  role: 'ro',
  password: randomPassword(),
  permissions: ['score'],
});

function renderPublicIdCell(publicId) {
  const parts = (publicId || '').split('|');
  const displayId = parts.length > 1 ? parts[1] : publicId;
  const countryCode = parts.length > 1 ? parts[0] : null;
  const flagSrc = countryCode ? getFlagSrc({ countryCode, size: 16 }) : null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {flagSrc && <img src={flagSrc} alt={countryCode} style={{ width: 16, height: 12 }} />}
      {displayId}
    </Box>
  );
}

export default function Staff() {
  const { matchId } = useParams();
  const { match } = useOutletContext();
  const realMatchId = match?.id || matchId;
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const addStaffFromNav = location.state?.addStaff;
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editIndex, setEditIndex] = useState(-1);
  const [editing, setEditing] = useState(false);
  const [current, setCurrent] = useState(emptyStaff());
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await getMatchStaff(matchId);
        setStaffList(Array.isArray(data) ? data : []);
        if (addStaffFromNav) {
          setEditIndex(-1);
          const pidParts = (addStaffFromNav.publicId || '').split('|');
          const countryCode = pidParts.length > 1 ? pidParts[0] : '';
          const publicIdNumber = pidParts.length > 1 ? pidParts.slice(1).join('|') : addStaffFromNav.publicId || '';
          setCurrent({ ...emptyStaff(), ...addStaffFromNav, countryCode, publicIdNumber });
          setEditing(true);
          navigate(location.pathname, { replace: true });
        }
      } catch (err) {
        setSnack({ open: true, message: t('manage.staffLoadFailed'), severity: 'error' });
      } finally {
        setLoading(false);
      }
    })();
  }, [matchId]);

  const handleAdd = () => {
    setEditIndex(-1);
    setCurrent(emptyStaff());
    setEditing(true);
  };

  const handleEdit = (idx) => {
    setEditIndex(idx);
    const member = staffList[idx];
    const userParts = (member.user || '').split('|');
    const password = userParts.length === 3 ? userParts[2] : '';
    const pidParts = (member.publicId || '').split('|');
    const countryCode = pidParts.length > 1 ? pidParts[0] : '';
    const publicIdNumber = pidParts.length > 1 ? pidParts.slice(1).join('|') : member.publicId || '';
    setCurrent({ ...member, password, countryCode, publicIdNumber });
    setEditing(true);
  };

  const handleRemove = (idx) => {
    const newList = staffList.filter((_, i) => i !== idx);
    setStaffList(newList);
  };

  const handleEditSave = () => {
    const { password, countryCode, publicIdNumber, ...staffData } = current;
    staffData.publicId = countryCode ? `${countryCode}|${publicIdNumber}` : publicIdNumber;
    staffData.user = `com.endofscoring|${realMatchId}|${password}`;
    const newList = [...staffList];
    if (editIndex >= 0) {
      newList[editIndex] = staffData;
    } else {
      newList.push(staffData);
    }
    setStaffList(newList);
    setEditing(false);
  };

  const handlePermissionToggle = (perm) => {
    const perms = [...(current.permissions || [])];
    const idx = perms.indexOf(perm);
    if (idx >= 0) {
      perms.splice(idx, 1);
    } else {
      perms.push(perm);
    }
    setCurrent({ ...current, permissions: perms });
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      await updateMatchStaff(matchId, staffList);
      setSnack({ open: true, message: t('common.saveSuccess'), severity: 'success' });
    } catch (err) {
      setSnack({ open: true, message: err.response?.data?.error || t('common.saveError'), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }

  if (editing) {
    return (
      <Container maxWidth="sm" sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <IconButton onClick={() => setEditing(false)}><BackIcon /></IconButton>
          <Typography variant="h5">{editIndex >= 0 ? t('manage.editStaffMember') : t('manage.addStaffMember')}</Typography>
        </Box>
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <CountrySelect
                value={current.countryCode}
                onChange={(code) => setCurrent({ ...current, countryCode: code })}
                label={t('regLanding.countryCode')}
                size="medium"
                sx={{ width: '45%' }}
              />
              <TextField
                label="ID"
                value={current.publicIdNumber}
                onChange={(e) => setCurrent({ ...current, publicIdNumber: e.target.value })}
                sx={{ flex: 1 }}
              />
            </Box>
            <TextField label={t('shootersPage.name')} value={current.name} onChange={(e) => setCurrent({ ...current, name: e.target.value })} fullWidth />
            <TextField label={t('manage.staffPassword')} value={current.password || ''} onChange={(e) => setCurrent({ ...current, password: e.target.value })} fullWidth />
            <FormControl fullWidth>
              <InputLabel>{t('manage.staffRole')}</InputLabel>
              <Select value={current.role} label={t('manage.staffRole')} onChange={(e) => setCurrent({ ...current, role: e.target.value })}>
                {ROLES.map((r) => <MenuItem key={r} value={r}>{ROLE_LABELS[r]}</MenuItem>)}
              </Select>
            </FormControl>
            <Typography variant="subtitle2">{t('manage.staffPermissions')}</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0 }}>
              {PERMISSIONS.map((perm) => (
                <FormControlLabel
                  key={perm}
                  control={
                    <Checkbox
                      checked={(current.permissions || []).includes(perm)}
                      onChange={() => handlePermissionToggle(perm)}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">{t(PERMISSION_KEYS[perm]) || perm}</Typography>}
                  sx={{ width: '50%' }}
                />
              ))}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
              <Button onClick={() => setEditing(false)}>{t('common.cancel')}</Button>
              <Button onClick={handleEditSave} variant="contained" disabled={!current.name || !current.publicIdNumber || !current.password}>{t('common.save')}</Button>
            </Box>
          </Box>
        </Paper>
        <Snackbar open={snack.open} message={snack.message} severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })} />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5">{t('matches.staff')}</Typography>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>{t('shootersPage.name')}</TableCell>
              <TableCell>{t('manage.staffRole')}</TableCell>
              <TableCell>{t('manage.staffPermissions')}</TableCell>
              <TableCell align="right">{t('manage.staffActions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {staffList.length === 0 && (
              <TableRow><TableCell colSpan={5} align="center">{t('manage.staffNoMembers')}</TableCell></TableRow>
            )}
            {staffList.map((member, idx) => (
              <TableRow key={idx}>
                <TableCell>{renderPublicIdCell(member.publicId)}</TableCell>
                <TableCell>{member.name}</TableCell>
                <TableCell><Chip label={ROLE_LABELS[member.role] || member.role} size="small" /></TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {(member.permissions || []).map((p) => (
                      <Chip key={p} label={t(PERMISSION_KEYS[p]) || p} size="small" variant="outlined" />
                    ))}
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" color="primary" onClick={() => handleEdit(idx)}><EditIcon fontSize="small" /></IconButton>
                  <IconButton size="small" color="error" onClick={() => handleRemove(idx)}><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAdd}>{t('manage.addStaff')}</Button>
        <SaveButton onClick={handleSaveAll} saving={saving} label={t('manage.saveStaff')} />
      </Box>

      <Snackbar open={snack.open} message={snack.message} severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })} />
    </Container>
  );
}
